import React, { useState } from 'react';
import { 
  Calendar, 
  Clock, 
  AlertTriangle, 
  ChevronRight, 
  Search, 
  Sliders, 
  Bell, 
  FileText, 
  Sparkles, 
  Hourglass, 
  Trash2,
  ExternalLink,
  ChevronDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useSavedSolicitations, SavedSolicitation } from '../SavedSolicitationsContext';
import { Card, Button, Badge, cn } from './SaaSUIHelper';

interface CriticalDeadlinesWidgetProps {
  onAnalyze?: (sol: any) => void;
  onNavigateToTab?: (tabId: string) => void;
}

export function CriticalDeadlinesWidget({ onAnalyze, onNavigateToTab }: CriticalDeadlinesWidgetProps) {
  const { savedList, loading, toggleSave, updateReminderPreference } = useSavedSolicitations();
  const [filterType, setFilterType] = useState<'all' | 'urgent' | 'approaching'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingReminderId, setEditingReminderId] = useState<string | null>(null);

  // Helper: Calculate days remaining
  const calculateDaysRemaining = (dueDateStr: string): number | null => {
    if (!dueDateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDateStr);
    due.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Process and sort solicitations
  const processedList = savedList
    .map(item => {
      const daysLeft = calculateDaysRemaining(item.dueDate);
      return {
        ...item,
        daysRemaining: daysLeft
      };
    })
    // Sort by: items with valid days remaining first, then lowest days remaining (soonest), then alphabetically
    .sort((a, b) => {
      if (a.daysRemaining === null && b.daysRemaining === null) return a.title.localeCompare(b.title);
      if (a.daysRemaining === null) return 1;
      if (b.daysRemaining === null) return -1;
      return a.daysRemaining - b.daysRemaining;
    });

  // Filter based on selected criteria
  const filteredList = processedList.filter(item => {
    if (!item) return false;
    const iTitle = item.title || "";
    const iAgency = item.agency || "";
    const iState = item.state || "";
    const matchesSearch = iTitle.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          iAgency.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          iState.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;

    if (filterType === 'urgent') {
      return item.daysRemaining !== null && item.daysRemaining <= 3 && item.daysRemaining >= 0;
    }
    if (filterType === 'approaching') {
      return item.daysRemaining !== null && item.daysRemaining <= 7 && item.daysRemaining >= 0;
    }
    return true;
  });

  const urgentCount = processedList.filter(item => item.daysRemaining !== null && item.daysRemaining <= 3 && item.daysRemaining >= 0).length;
  const approachingCount = processedList.filter(item => item.daysRemaining !== null && item.daysRemaining <= 7 && item.daysRemaining >= 0).length;

  return (
    <Card className="p-6 h-full flex flex-col justify-between shadow-sm relative overflow-hidden" id="critical-deadlines-widget">
      <div className="absolute -right-12 -top-12 w-32 h-32 bg-indigo-50/50 rounded-full blur-2xl pointer-events-none" />
      
      <div>
        {/* Header Section */}
        <div className="flex justify-between items-start mb-4">
          <div className="space-y-1">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Hourglass className="w-5 h-5 text-indigo-600 animate-pulse" />
              Critical Deadlines Watch
            </h2>
            <p className="text-xs text-gray-500">
              Track and configure high-priority response submission countdowns.
            </p>
          </div>
          {urgentCount > 0 && (
            <Badge variant="danger" className="animate-bounce shrink-0">
              {urgentCount} Critical
            </Badge>
          )}
        </div>

        {/* Filters and Search Bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Filter saved deadlines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setFilterType('all')}
              className={cn(
                "px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer",
                filterType === 'all' 
                  ? "bg-indigo-600 text-white border-indigo-600" 
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              )}
            >
              All ({savedList.length})
            </button>
            <button
              onClick={() => setFilterType('urgent')}
              className={cn(
                "px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer flex items-center gap-1",
                filterType === 'urgent' 
                  ? "bg-rose-600 text-white border-rose-600" 
                  : "bg-white text-rose-600 border-gray-200 hover:bg-rose-50/50"
              )}
            >
              Urgent ({urgentCount})
            </button>
            <button
              onClick={() => setFilterType('approaching')}
              className={cn(
                "px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer",
                filterType === 'approaching' 
                  ? "bg-amber-600 text-white border-amber-600" 
                  : "bg-white text-amber-600 border-gray-200 hover:bg-amber-50/50"
              )}
            >
              Soon ({approachingCount})
            </button>
          </div>
        </div>

        {/* Deadlines List */}
        {loading ? (
          <div className="py-12 flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-[11px] text-gray-400">Syncing with Watchlist...</p>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="py-10 text-center space-y-3 bg-gray-50/50 rounded-xl border border-dashed border-gray-100 p-4">
            <Calendar className="w-8 h-8 text-gray-300 mx-auto" />
            <div className="space-y-1">
              <p className="text-xs font-bold text-gray-800">No deadlines identified</p>
              <p className="text-[11px] text-gray-500 max-w-[250px] mx-auto">
                {searchQuery || filterType !== 'all' 
                  ? "No matching items found. Reset search parameters or change active filters." 
                  : "Opportunities you bookmark as 'saved' will automatically populate this dynamic tracking module."}
              </p>
            </div>
            {(!searchQuery && filterType === 'all' && onNavigateToTab) && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onNavigateToTab('solicitations')}
                className="mt-1 cursor-pointer text-[11px] h-8 py-1"
              >
                Find Opportunities
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3.5 max-h-[340px] overflow-y-auto pr-1">
            <AnimatePresence initial={false}>
              {filteredList.map((item) => {
                const isUrgent = item.daysRemaining !== null && item.daysRemaining <= 3 && item.daysRemaining >= 0;
                const isApproaching = item.daysRemaining !== null && item.daysRemaining <= 7 && item.daysRemaining > 3;
                const isOverdue = item.daysRemaining !== null && item.daysRemaining < 0;
                
                // Urgency-based color themes
                let themeColor = 'indigo';
                let progressPercentage = 100;
                
                if (isUrgent) {
                  themeColor = 'rose';
                  progressPercentage = Math.max(10, (item.daysRemaining! / 3) * 100);
                } else if (isApproaching) {
                  themeColor = 'amber';
                  progressPercentage = Math.max(30, (item.daysRemaining! / 7) * 100);
                } else if (isOverdue) {
                  themeColor = 'gray';
                  progressPercentage = 0;
                } else if (item.daysRemaining !== null) {
                  themeColor = 'indigo';
                  progressPercentage = Math.min(100, (item.daysRemaining / 30) * 100);
                }

                return (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className={cn(
                      "p-3.5 rounded-xl border transition-all text-xs relative",
                      isUrgent && "bg-rose-50/20 border-rose-100 hover:bg-rose-50/30",
                      isApproaching && "bg-amber-50/10 border-amber-100 hover:bg-amber-50/20",
                      isOverdue && "bg-gray-50/40 border-gray-200 opacity-60",
                      (!isUrgent && !isApproaching && !isOverdue) && "bg-white border-gray-100 hover:border-gray-250"
                    )}
                  >
                    {/* Item header */}
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <div className="space-y-0.5 min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant={item.state === 'N/A' ? 'default' : 'info'} className="text-[9px] px-1.5">
                            {item.state}
                          </Badge>
                          <span className="text-[10px] text-gray-400 font-medium truncate max-w-[150px]">
                            {item.agency}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-900 tracking-tight leading-tight truncate pr-1" title={item.title}>
                          {item.title}
                        </h4>
                      </div>
                      
                      {/* Days countdown tag */}
                      <span className={cn(
                        "font-black px-2 py-0.5 rounded-md text-[10px] uppercase border shrink-0 text-right",
                        isUrgent && "bg-rose-100 text-rose-800 border-rose-200 animate-pulse",
                        isApproaching && "bg-amber-100 text-amber-800 border-amber-200",
                        isOverdue && "bg-gray-100 text-gray-500 border-gray-200",
                        (!isUrgent && !isApproaching && !isOverdue) && "bg-indigo-50 text-indigo-700 border-indigo-150"
                      )}>
                        {item.daysRemaining === null ? 'No Date' :
                         item.daysRemaining < 0 ? 'Closed' :
                         item.daysRemaining === 0 ? '⏰ Due Today' :
                         item.daysRemaining === 1 ? '⏰ Tomorrow' :
                         `${item.daysRemaining} Days`}
                      </span>
                    </div>

                    {/* Progress slider bar representation */}
                    {item.daysRemaining !== null && item.daysRemaining >= 0 && (
                      <div className="space-y-1 mb-2.5">
                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={cn(
                              "h-full rounded-full transition-all duration-500",
                              themeColor === 'rose' && "bg-rose-500",
                              themeColor === 'amber' && "bg-amber-500",
                              themeColor === 'indigo' && "bg-indigo-600"
                            )}
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Bottom toolbar & interactions */}
                    <div className="flex justify-between items-center text-[11px] pt-2 border-t border-gray-100">
                      
                      {/* Reminder preference triggers */}
                      <div className="relative">
                        {editingReminderId === item.id ? (
                          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-0.5 shadow-sm">
                            <span className="text-[10px] text-gray-400 px-1">Alert threshold:</span>
                            {[1, 3, 5, 7].map(days => (
                              <button
                                key={days}
                                onClick={() => {
                                  updateReminderPreference(item.solicitationId, days);
                                  setEditingReminderId(null);
                                }}
                                className={cn(
                                  "w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold cursor-pointer transition-colors",
                                  item.reminderDaysBefore === days 
                                    ? "bg-indigo-600 text-white" 
                                    : "text-gray-600 hover:bg-gray-100"
                                )}
                              >
                                {days}
                              </button>
                            ))}
                            <button
                              onClick={() => setEditingReminderId(null)}
                              className="text-gray-400 hover:text-gray-600 text-[10px] px-1 border-l border-gray-100 ml-0.5 cursor-pointer"
                            >
                              Close
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingReminderId(item.id)}
                            className="text-gray-500 hover:text-indigo-600 font-medium flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <Bell className="w-3.5 h-3.5" />
                            <span>Alert: {item.reminderDaysBefore || 3}d before</span>
                            <ChevronDown className="w-3 h-3 text-gray-400" />
                          </button>
                        )}
                      </div>

                      {/* Quick CTA Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleSave({ solicitationId: item.solicitationId, title: item.title })}
                          className="text-gray-400 hover:text-rose-600 p-1 rounded hover:bg-gray-50 cursor-pointer"
                          title="Remove bookmark"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {onAnalyze && !isOverdue && (
                          <button
                            onClick={() => {
                              // Wrap minimal solicitation detail needed by onAnalyze callback
                              onAnalyze({
                                solicitationId: item.solicitationId,
                                title: item.title,
                                state: item.state,
                                agency: item.agency,
                                dueDate: item.dueDate
                              });
                            }}
                            className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold px-2 py-1 rounded-md flex items-center gap-0.5 cursor-pointer transition-all"
                          >
                            <span>Draft RFX</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        )}
                      </div>

                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Widget Footer Status Information */}
      {!loading && savedList.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
            AI reminders synced automatically.
          </span>
          {onNavigateToTab && (
            <button 
              onClick={() => onNavigateToTab('solicitations')}
              className="text-indigo-600 hover:text-indigo-700 font-bold cursor-pointer hover:underline flex items-center"
            >
              Configure Reminders <ChevronRight className="w-3 h-3 ml-0.5" />
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
